import { useCallback, useEffect, useRef, useState } from 'react';

import { Autocomplete } from '@/components/Inputs';
import {
  type AutocompletePrediction,
  fetchAutocompletePredictions as fetchPredictions,
  fetchPlaceDetails,
  type PredictionOptions,
} from '@/store/places/places.api';

export const AddressInput = ({
  initialInput,
  address,
  onPlaceChange,
  className,
  label,
  placeholder,
  searchOptions,
}: {
  initialInput: string;
  /**
   * An address set somewhere else, such as a tap on a map, shown as the picked one. It is not
   * looked up, so the place stays where it was set. Its × reports `null`, like any clear; with
   * this set there is no box to type in, so that `null` can only be the ×.
   */
  address?: string;
  onPlaceChange(place: null | PlaceDetails): void;
  className?: string;
  label: string;
  placeholder?: string;
  searchOptions?: PredictionOptions;
}) => {
  const [place, setPlace] = useState<null | PlaceDetails>(null);

  const handleLocationChange = useCallback(
    (place: null | PlaceDetails) => {
      setPlace(place);
      onPlaceChange(place);
    },
    [onPlaceChange],
  );

  return (
    <Address
      place={place}
      address={address}
      onPlaceChange={handleLocationChange}
      initialInput={initialInput}
      className={className}
      label={label}
      placeholder={placeholder}
      searchOptions={searchOptions}
    />
  );
};

const Address = ({
  place,
  address,
  onPlaceChange,
  initialInput,
  className,
  label,
  placeholder,
  searchOptions,
}: {
  place: null | PlaceDetails;
  address?: string;
  onPlaceChange: (place: null | PlaceDetails) => void;
  initialInput?: string;
  className?: string;
  label: string;
  placeholder?: string;
  searchOptions?: PredictionOptions;
}) => {
  const [input, setInput] = useState(initialInput ?? '');
  const [predictions, setPredictions] = useState<null | AutocompletePrediction[]>(null);
  const initialInputRef = useRef(initialInput);
  const searchOptionsRef = useRef(searchOptions);
  // Ref the callback so the mount effect stays mount-only even when the parent
  // passes a fresh onPlaceChange each render — otherwise it loops (spams Places).
  const handlePlaceClickRef = useRef<(placeId: null | string) => void>(() => {});

  const handleInput = async (input: string) => {
    setInput(input);
    onPlaceChange(null);
    setPredictions(null);
    try {
      const predictions = await fetchAutocompletePredictions(input, searchOptions);
      setPredictions(predictions);
    } catch (err: unknown) {
      console.error(err);
    }
  };

  const handlePlaceClick = useCallback(
    async (placeId: null | string) => {
      try {
        const place = placeId ? await fetchPlaceDetails(placeId) : null;
        if (place) {
          onPlaceChange(place);
        } else {
          onPlaceChange(null);
        }
      } catch (err: unknown) {
        console.error(err);
      }
    },
    [onPlaceChange],
  );

  useEffect(() => {
    handlePlaceClickRef.current = handlePlaceClick;
  });

  useEffect(() => {
    const initialInputCurrent = initialInputRef.current;
    if (!initialInputCurrent) {
      return;
    }
    (async () => {
      const [prediction] = await fetchAutocompletePredictions(
        initialInputCurrent,
        searchOptionsRef.current,
      );
      if (prediction) {
        handlePlaceClickRef.current(prediction.placeId);
      }
    })();
  }, []);

  return (
    <Autocomplete
      label={label}
      placeholder={placeholder ?? label}
      options={
        predictions?.map(({ description, placeId }) => ({ label: description, value: placeId })) ??
        []
      }
      className={className}
      onChange={handleInput}
      value={input}
      selectedOptionLabel={address ?? place?.formattedAddress}
      onItemSelect={handlePlaceClick}
    />
  );
};

export type { AutocompletePrediction, PredictionOptions };

export interface PlaceDetails {
  id: string;
  displayName?: {
    text: string;
  };
  formattedAddress?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  addressComponents?: Array<{
    types?: string[];
    longText?: string;
    shortText?: string;
    text?: string;
  }>;
}

// Default = US localities (the city picker). Callers pass options to widen the search,
// e.g. global street addresses for "where do you operate". Region filter is omitted when empty.
const fetchAutocompletePredictions = (input: string, options?: PredictionOptions) =>
  fetchPredictions(input, {
    includedPrimaryTypes: options?.includedPrimaryTypes ?? ['locality'],
    includedRegionCodes: options ? options.includedRegionCodes : ['us'],
  });
